<template>
  <div class="half-donut-chart-wrapper">
    <div v-if="legendActive" class="chart-legend my-2">
      <div
        v-for="segment in segments"
        :key="segment.key"
        class="legend-item"
      >
        <div class="legend-value">{{ formatValue(segment.value) }}</div>
        <div class="legend-label">
          <span class="legend-dot" :style="{ backgroundColor: segment.color }"></span>
          <span>{{ labelsMap[segment.key] || segment.key }}</span>
        </div>
      </div>
    </div>
    <div class="half-donut-chart" :style="{ width: `${size}px`, height: `${height}px` }">
      <highcharts
        class="half-donut-chart__canvas"
        :highcharts="Highcharts"
        :options="chartOptions"
      />
      <div class="chart-center">
        <div class="center-value">{{ totalValue }}</div>
        <div v-if="totalLabel" class="center-label">{{ totalLabel }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import { Chart } from "highcharts-vue";

export default {
  name: "HalfDonutChart",
  components: {
    highcharts: Chart
  },
  props: {
    segments: {
      type: Array,
      required: true,
      default: () => []
    },
    totalValue: {
      type: [Number, String],
      required: true
    },
    totalLabel: {
      type: String,
      default: ""
    },
    labels: {
      type: Object,
      default: () => ({})
    },
    size: {
      type: Number,
      default: 220
    },
    height: {
      type: Number,
      default: 120
    },
    strokeWidth: {
      type: Number,
      default: 32
    }
  },
  data() {
    return {
      Highcharts
    };
  },
  computed: {
    chartHeight() {
      return Math.round(this.height * 1.4);
    },
    total() {
      return this.segments.reduce((sum, segment) => sum + (segment.value || 0), 0);
    },
    seriesData() {
      if (!this.total) {
        return [
          {
            y: 1,
            color: "#E2E8F0",
            name: "empty",
            enableMouseTracking: false,
            dataLabels: { enabled: false }
          }
        ];
      }
      return this.segments.map(segment => ({
        y: segment.value || 0,
        color: segment.color,
        name: segment.key
      }));
    },
    innerSizePercent() {
      const outerRadius = this.size / 2;
      const innerRadius = Math.max(outerRadius - this.strokeWidth, 0);
      return `${Math.round((innerRadius / outerRadius) * 100)}%`;
    },
    chartOptions() {
      return {
        chart: {
          type: "pie",
          backgroundColor: "transparent",
          height: this.chartHeight,
          width: this.size,
          margin: [0, 0, -Math.round(this.height * 0.45), 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: null },
        credits: { enabled: false },
        tooltip: { enabled: false },
        accessibility: { enabled: false },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 90,
            center: ["50%", "65%"],
            size: this.size,
            innerSize: this.innerSizePercent,
            borderWidth: 0,
            dataLabels: { enabled: false },
            states: {
              inactive: { opacity: 1 },
              hover: { enabled: false }
            }
          }
        },
        series: [
          {
            type: "pie",
            data: this.seriesData
          }
        ]
      };
    },
    legendActive() {
      return Array.isArray(this.segments) && this.segments.length > 0;
    },
    labelsMap() {
      return this.labels || {};
    }
  },
  methods: {
    formatValue(value) {
      if (value === null || value === undefined) {
        return "0";
      }

      const num = Number(value);

      if (!Number.isFinite(num)) {
        return "0";
      }

      return Math.max(0, num).toString();
    }
  }
};
</script>

<style scoped>
.half-donut-chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.half-donut-chart {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
}

.half-donut-chart__canvas ::v-deep .highcharts-background {
  fill: transparent;
}

.chart-center {
  position: absolute;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.chart-legend {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.legend-item {
  text-align: center;
}

.legend-value {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.legend-label {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
}

.center-value {
  font-size: 26px;
  font-weight: 600;
  color: #0f172a;
}

.center-label {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
}
</style>
